import {inject, Injectable, signal} from '@angular/core';
import {BehaviorSubject, catchError, Observable} from 'rxjs';
import {CommunicationService} from '../http/communication.service';
import {AuthorizationService} from './authorization.service';
import {Permissions} from '../shared/permissions';
import {StorageService} from './storage.service';
import {LocalStorageKeys} from '../shared/types/storage';
import { UserProfileResponse } from '../shared/types/UserProfile';

export interface AccountOption {
  name: string;
  key: string;
}

@Injectable({
  providedIn: 'root',
})

export class StateService {
  private communicationService = inject(CommunicationService);
  private authorizationService = inject(AuthorizationService);
  private storageService = inject(StorageService);
  public isRouterOutletScrolledToTop = signal<boolean>(true);
  private loadingTriggersCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private _accounts: AccountOption[] = [];
  private _activeAccount = signal<string>(this.storageService.local.getItem(LocalStorageKeys.ACCOUNT) || '');

  readonly activeAccount = this._activeAccount.asReadonly();
  loading$ = this.loadingSubject.asObservable();

  constructor() {
    this.loadingTriggersCountSubject.subscribe(count => {
      this.loadingSubject.next(count > 0);
    });
  }

  get accounts(): AccountOption[] {
    return this._accounts;
  }

  set accounts(accounts: AccountOption[]) {
    this._accounts = accounts;
  }

  set loading(value: boolean) {
    setTimeout(() => {
      const nextValue = Math.max(0, this.loadingTriggersCountSubject.getValue() + (value ? 1 : -1));
      this.loadingTriggersCountSubject.next(nextValue);
    });
  }

  public getMyUser(): Observable<UserProfileResponse> {
    return this.communicationService.httpGet<UserProfileResponse>('/users/my-user').pipe(
      catchError((error) => {
        console.error('Error fetching my user', error);
        throw error;
      })
    );
  }

  public updateAccountsOnUserUpdate(account: { key: string; name?: string }, email: string) {
    const accountName = account.name || account.key;
    if (this.authorizationService.hasPermissions([Permissions.READ_ACCOUNT])) {
      const existingKeys = this.accounts.map(acc => acc.key);
      if (!existingKeys.includes(account.key)) {
        this.accounts = [...this.accounts, { key: account.key, name: accountName }];
      }
    } else if (email === this.authorizationService.userProfile.email) {
      this.accounts = [{ key: account.key, name: accountName }];
    }
  }

  public setActiveAccount(org: string): void {
    this.storageService.local.setItem(LocalStorageKeys.ACCOUNT, org);
    this._activeAccount.set(org);
  }
}
